var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { DrawerContainer, DrawerHandle, DrawerCard, DrawerTitle, DrawerFooter, DrawerContent, DrawerImage, DrawerOverflow, DrawerCloseButton, DrawerBackdrop, DrawerFooterBorder, DrawerStickyAction, DrawerStickyBuffer, } from './elements';
import useDrawerBehavior from './useDrawerBehavior';
import { useTrapFocus, useCombinedRefs, noop, useDisableBodyScroll, } from '../../util';
export var Drawer = React.forwardRef(function (_a, fRef) {
    var open = _a.open, title = _a.title, imgSrc = _a.imgSrc, footer = _a.footer, StickyAction = _a.stickyAction, children = _a.children, _b = _a.onOpenUpdate, onOpenUpdate = _b === void 0 ? noop : _b, rest = __rest(_a, ["open", "title", "imgSrc", "footer", "stickyAction", "children", "onOpenUpdate"]);
    var containerRef = useRef(null);
    var backdropRef = useRef(null);
    var contentRef = useRef(null);
    var stickyActionRef = useRef(null);
    var _c = useState(NaN), stickyActionHeight = _c[0], setStickyActionHeight = _c[1];
    var _d = useState(false), shrinked = _d[0], setShrinked = _d[1];
    var drawerBehavior = useDrawerBehavior(containerRef, backdropRef, contentRef, open, onOpenUpdate);
    var innerRef = React.useRef();
    var combinedRef = useCombinedRefs(fRef, innerRef);
    useTrapFocus(innerRef);
    useEffect(function () {
        var isShrinked = function () {
            if (contentRef.current) {
                var _a = contentRef.current, offsetHeight = _a.offsetHeight, scrollHeight = _a.scrollHeight;
                setShrinked(scrollHeight > offsetHeight);
            }
        };
        window.addEventListener('resize', isShrinked);
        return function () {
            window.removeEventListener('resize', isShrinked);
        };
    }, [shrinked]);
    useLayoutEffect(function () {
        if (!window.ResizeObserver)
            return;
        if (stickyActionRef.current) {
            setStickyActionHeight(stickyActionRef.current.offsetHeight);
            var footerObserver_1 = new ResizeObserver(function () {
                requestAnimationFrame(function () {
                    setStickyActionHeight(stickyActionRef.current.offsetHeight);
                });
            });
            footerObserver_1.observe(stickyActionRef.current);
            return function () {
                footerObserver_1.disconnect;
            };
        }
    }, [stickyActionRef]);
    var showContent = function () { return imgSrc || children; };
    useDisableBodyScroll(open);
    return (React.createElement(React.Fragment, null,
        React.createElement(DrawerOverflow, __assign({ ref: combinedRef, isOpen: open }, rest),
            React.createElement(DrawerBackdrop, { ref: backdropRef, isOpen: open, onClick: drawerBehavior.onBackdropClick }),
            React.createElement(DrawerContainer, { "$stickyAction": StickyAction, isOpen: open, ref: containerRef, role: "status", "aria-live": "assertive", "aria-hidden": !open },
                React.createElement(DrawerHandle, { "$stickyAction": StickyAction }),
                React.createElement(DrawerCard, { "$stickyActionHeight": stickyActionHeight },
                    open && (React.createElement(DrawerCloseButton, { "aria-label": "close drawer", onClick: drawerBehavior.close, onTouchEnd: drawerBehavior.close })),
                    title && React.createElement(DrawerTitle, null, title),
                    showContent() && (React.createElement(DrawerContent, { ref: contentRef },
                        imgSrc && React.createElement(DrawerImage, { imgSrc: imgSrc }),
                        children && (React.createElement("div", { style: { padding: '16px' } }, children)))),
                    showContent() && footer && shrinked && React.createElement(DrawerFooterBorder, null),
                    footer && React.createElement(DrawerFooter, __assign({}, footer))))),
        StickyAction && (React.createElement(React.Fragment, null,
            React.createElement(DrawerStickyBuffer, null),
            React.createElement(DrawerStickyAction, { ref: stickyActionRef },
                React.createElement(StickyAction, null))))));
});
